/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · Till → Shift → Business-Day close orchestrator.
 *
 * Purpose
 *   Fix the mis-sequenced close journey. Historically the operator was
 *   dropped straight into the shift denomination form; only after
 *   submitting did the server reject the close because a till was still
 *   open. This module inverts the sequence:
 *
 *     Close Shift → check open tills FIRST → resolve each till →
 *       (no open tills) → aggregate summary + Manager PIN + Z-report.
 *
 * Components exported
 *   • {@link TillShiftCloseFlow} — the entry orchestrator that FloorView
 *     mounts when the operator taps "Close Shift". It polls the
 *     server's open-till list (single source of truth — never a
 *     separate reducer inventory) and renders the correct sub-dialog:
 *     the blocker while open tills remain, then the aggregated
 *     summary once every till carries a closed reconciliation.
 *   • {@link TillCloseDenominationDialog} — reuses the ₹500 → ₹1 grid
 *     that used to live inside `ShiftCloseDialog`. Handles both the
 *     cashier SELF close and the manager takeover close paths (the
 *     latter simply drops in a Reason field + calls the server with
 *     `close_method = MANAGER_TAKEOVER`).
 *   • {@link FloatReturnDialog} — the shortcut close for a till with
 *     zero transactions but a live opening float. Manager PIN +
 *     explicit "float returned" confirmation, no denomination entry.
 *
 * Contract with the rest of the app
 *   • Reuses `livePos.listTills`, `livePos.closeTill`, `livePos.closeShift`,
 *     `livePos.tillTakeoverLock` — every server call goes through the
 *     existing thunk layer, not new endpoints.
 *   • Manager PIN is captured through the same shared `SharedPinDialog`
 *     the rest of the terminal uses via a `requestAuth(reason, done)`
 *     callback passed by the caller.
 *   • Never nets one cashier's variance against another. Variance is
 *     persisted per till on the till document itself.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertTriangle, Banknote, User, Clock, ShieldCheck, XCircle, CheckCircle2, RefreshCcw, Lock } from "lucide-react";

const DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1];
const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ─── Denomination grid (reused across cashier + manager closes) ────
function DenominationGrid({ denoms, setDenoms, testidPrefix = "pos-till-denom" }) {
  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-grid`}>
      {DENOMINATIONS.map((d) => (
        <div key={d} className="flex items-center gap-2">
          <div className="w-16 text-right text-[13px] font-medium tabular-nums">₹{d}</div>
          <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)] text-[10px]">×</span>
          <input
            type="number" min={0}
            value={denoms[d] || ""}
            onChange={(e) => setDenoms({ ...denoms, [d]: e.target.value })}
            className="flex-1 h-9 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-2 text-[13px] tabular-nums focus:border-slate-500 focus:outline-none"
            data-testid={`${testidPrefix}-${d}`} />
          <div className="w-24 text-right text-[13px] tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">
            ₹{fmt2(d * Number(denoms[d] || 0))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── TillCloseDenominationDialog ───────────────────────────────────
// Two-mode dialog:
//   mode === "SELF"              — cashier closing their own till
//   mode === "MANAGER_TAKEOVER"  — manager closing a cashier's till
//                                  (locks the till first, requires a
//                                  takeover reason, persists close_method)
export function TillCloseDenominationDialog({
  open, till, mode = "SELF", onCancel, onSubmit, requestAuth,
}) {
  const [denoms, setDenoms] = useState({});
  const [reason, setReason] = useState("");
  const [note,   setNote]   = useState("");
  const [takeoverReason, setTakeoverReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setDenoms({}); setReason(""); setNote(""); setTakeoverReason("");
  }, [open]);

  const counted = useMemo(
    () => Object.entries(denoms).reduce((s, [d, q]) => s + Number(d) * Number(q || 0), 0),
    [denoms],
  );
  const summary  = till?.settlement_summary || {};
  const cashSales    = Number(summary.cash_sales    || 0);
  const openingFloat = Number(till?.opening_float   || 0);
  const expected     = Number(summary.expected_cash || (openingFloat + cashSales));
  const variance     = counted - expected;
  const varianceThreshold = 20;   // ₹ — same as the legacy shift close
  const overThreshold = Math.abs(variance) > varianceThreshold;
  const canSubmit = (!overThreshold || reason.trim().length > 0)
                 && (mode !== "MANAGER_TAKEOVER" || takeoverReason.trim().length > 0);

  const submit = () => {
    if (!canSubmit) return;
    const payload = {
      till_id:                  till.id,
      close_method:             mode,
      denominations:            { ...denoms },
      counted_cash:             counted,
      expected_cash:            expected,
      cash_variance:            variance,
      variance_reason:          overThreshold ? (reason.trim() || "OTHER") : null,
      variance_note:            note.trim() || null,
      settlement_mode_summary:  summary,
      float_returned:           openingFloat,
    };
    if (mode === "MANAGER_TAKEOVER") {
      // Manager PIN dialog is chained here so the same SharedPinDialog
      // the rest of the terminal uses gates the close. `requestAuth`
      // is passed by the FloorView (single source of truth for the
      // PIN capture UX).
      requestAuth(
        `Manager takeover close · till ${till.id.slice(0, 6)}… · reason ${takeoverReason}`,
        async (auth) => {
          onSubmit({
            ...payload,
            manager_authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
            takeover_reason: takeoverReason.trim(),
          });
        },
      );
      return;
    }
    onSubmit(payload);
  };

  if (!open || !till) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[820px] w-[92vw] max-h-[92vh] p-0 gap-0 border border-slate-300 overflow-hidden flex flex-col"
        data-testid="pos-till-close-dialog"
        data-mode={mode}>
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] bg-slate-50 space-y-0 shrink-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            Close till · {till.staff_name || "cashier"}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            {mode === "MANAGER_TAKEOVER"
              ? "Manager takeover — cashier's transactions are frozen while you count and close."
              : "Count the physical drawer. Opening float is inside the drawer and belongs in the count."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1 font-semibold">
              Denomination count · Cash
            </div>
            <DenominationGrid denoms={denoms} setDenoms={setDenoms} testidPrefix="pos-till-denom" />
            <div className="mt-3 pt-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">Counted cash</span>
              <span className="text-[22px] font-semibold tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]"
                data-testid="pos-till-counted">
                ₹{fmt2(counted)}
              </span>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1 font-semibold">
              Expected cash
            </div>
            <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-3 space-y-1 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Opening float</span>
                <span className="tabular-nums">₹{fmt2(openingFloat)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Cash tenders</span>
                <span className="tabular-nums">₹{fmt2(cashSales)}</span>
              </div>
              <div className="mt-1 pt-1 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between font-semibold">
                <span>Expected cash</span>
                <span className="tabular-nums" data-testid="pos-till-expected">₹{fmt2(expected)}</span>
              </div>
              <div className="mt-2 pt-1 border-t border-dashed border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between font-semibold text-[13px]">
                <span>Cash variance</span>
                <span className={`tabular-nums ${overThreshold ? (variance > 0 ? "text-amber-800" : "text-rose-800") : "text-emerald-800"}`}
                  data-testid="pos-till-variance">
                  {variance >= 0 ? "+" : ""}₹{fmt2(variance)}
                </span>
              </div>
            </div>

            <div className="mt-4 text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold">
              Settlement modes (this till)
            </div>
            <div className="mt-1 space-y-1 text-[11px] tabular-nums">
              {Object.entries(summary.modes || {}).length === 0 && (
                <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No tenders recorded on this till.</div>
              )}
              {Object.entries(summary.modes || {}).map(([m, cell]) => (
                <div key={m} className="flex items-center justify-between">
                  <span className="uppercase text-[color:var(--pos-color-secondary-text,#5C6B64)]">{m}</span>
                  <span>{cell.count}× ₹{fmt2(cell.total)}</span>
                </div>
              ))}
            </div>

            {mode === "MANAGER_TAKEOVER" && (
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Takeover reason (required)
                </label>
                <textarea rows={2} value={takeoverReason}
                  onChange={(e) => setTakeoverReason(e.target.value)}
                  placeholder="e.g. cashier left · handover mid-shift · connectivity loss"
                  className="w-full mt-1 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-2 text-[12px] focus:outline-none focus:border-slate-500"
                  data-testid="pos-till-takeover-reason" />
              </div>
            )}

            {overThreshold && (
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Variance reason (required — {variance > 0 ? "over" : "short"} by ₹{fmt2(Math.abs(variance))})
                </label>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. tip left in drawer, incorrect change given"
                  className="w-full mt-1 h-9 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white px-2 text-[12px] focus:outline-none focus:border-slate-500"
                  data-testid="pos-till-variance-reason" />
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note for the audit"
                  className="w-full mt-1 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-2 text-[12px] focus:outline-none focus:border-slate-500"
                  data-testid="pos-till-variance-note" />
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
          <button type="button" onClick={onCancel}
            data-testid="pos-till-close-cancel"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            Cancel
          </button>
          <button type="button" disabled={!canSubmit} onClick={submit}
            data-testid="pos-till-close-submit"
            className="h-9 px-4 rounded bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {mode === "MANAGER_TAKEOVER" ? "Manager PIN → Close till" : "Close till"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── FloatReturnDialog ────────────────────────────────────────────
// Shortcut for a till with no transactions — Manager PIN + confirm.
export function FloatReturnDialog({ open, till, onCancel, onSubmit, requestAuth }) {
  if (!open || !till) return null;
  const opening = Number(till.opening_float || 0);
  const cashSales = Number(till?.settlement_summary?.cash_sales || 0);
  const anyTx = cashSales > 0 || Object.keys(till?.settlement_summary?.modes || {}).length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-amber-200 overflow-hidden"
        data-testid="pos-till-float-return-dialog">
        <DialogHeader className="px-5 py-3 border-b border-amber-100 bg-amber-50 space-y-0">
          <DialogTitle className="text-[11px] uppercase tracking-wider text-amber-900 font-normal">Return float &amp; close</DialogTitle>
          <DialogDescription className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {till.staff_name || "Cashier"}&rsquo;s till · opening float ₹{fmt2(opening)}
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-2 text-[12px]">
          {anyTx ? (
            <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>This till has recorded transactions. Use the full close flow (denomination count) instead.</span>
            </div>
          ) : (
            <>
              <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-3 text-[12px]">
                {till.staff_name || "Cashier"}&rsquo;s till has ₹{fmt2(opening)} opening float and no transactions.
                Confirm that the ₹{fmt2(opening)} float has been returned before closing this till.
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
          <button type="button" onClick={onCancel}
            data-testid="pos-till-float-cancel"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            Cancel
          </button>
          <button type="button" disabled={anyTx}
            data-testid="pos-till-float-confirm"
            onClick={() => requestAuth(
              `Return float ₹${fmt2(opening)} and close ${till.staff_name || "cashier"}'s till.`,
              async (auth) => {
                onSubmit({
                  till_id:                        till.id,
                  close_method:                   "FLOAT_ONLY_MANAGER",
                  manager_authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
                  takeover_reason:                "FLOAT_RETURN",
                  counted_cash:                   0,
                  expected_cash:                  0,
                  cash_variance:                  0,
                  denominations:                  {},
                  float_returned:                 opening,
                  settlement_mode_summary:        till.settlement_summary || {},
                });
              },
            )}
            className="h-9 px-4 rounded bg-amber-700 text-white text-[12px] font-semibold hover:bg-amber-800 disabled:opacity-40 disabled:cursor-not-allowed">
            <Lock className="w-3 h-3 inline mr-1" /> Manager PIN → Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── OpenTillsBlocker ─────────────────────────────────────────────
// Renders the list of still-open tills for the current shift. Each row
// gets its own per-cashier actions. Auto-refreshes every 5 s so a
// cashier closing their own till on another terminal drops off the list
// without the manager having to reload.
function OpenTillsBlocker({ open, tills, currentStaffId, onWaitAgain,
                            onSelfClose, onManagerTakeover, onFloatReturn }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onWaitAgain("cancel"); }}>
      <DialogContent className="max-w-[760px] w-[92vw] max-h-[86vh] p-0 gap-0 border border-amber-300 overflow-hidden flex flex-col"
        data-testid="pos-open-tills-blocker">
        <DialogHeader className="px-5 py-3 border-b border-amber-100 bg-amber-50 space-y-0 shrink-0">
          <DialogTitle className="text-[11px] uppercase tracking-wider text-amber-900 font-normal">Shift cannot close yet</DialogTitle>
          <DialogDescription className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {tills.length} open till{tills.length === 1 ? "" : "s"} — close each cashier before the shift terminates.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {tills.map((t) => {
            const isMine = t.staff_id === currentStaffId;
            const summary = t.settlement_summary || {};
            const cashSales = Number(summary.cash_sales || 0);
            const anyTx = cashSales > 0 || Object.keys(summary.modes || {}).length > 0;
            return (
              <div key={t.id}
                data-testid={`pos-open-till-row-${t.id}`}
                className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white p-3 flex items-center gap-3">
                <div className="shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] truncate"
                    data-testid={`pos-open-till-cashier-${t.id}`}>
                    {t.staff_name || "Cashier"} {isMine && <span className="text-[10px] uppercase tracking-wider text-emerald-700 ml-1">(you)</span>}
                  </div>
                  <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span>Float <strong className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">₹{fmt0(t.opening_float)}</strong></span>
                    <span>Cash <strong className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]">₹{fmt0(cashSales)}</strong></span>
                    {t.takeover_active_at && (
                      <span className="text-rose-800 flex items-center gap-1"><Lock className="w-3 h-3" /> takeover locked</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                  {isMine && (
                    <button type="button" onClick={() => onSelfClose(t)}
                      data-testid={`pos-open-till-self-close-${t.id}`}
                      className="h-9 px-3 rounded bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800">
                      Close till
                    </button>
                  )}
                  {!isMine && !anyTx && (
                    <button type="button" onClick={() => onFloatReturn(t)}
                      data-testid={`pos-open-till-float-return-${t.id}`}
                      className="h-9 px-3 rounded bg-amber-700 text-white text-[11px] font-semibold hover:bg-amber-800">
                      Return float &amp; close
                    </button>
                  )}
                  {!isMine && anyTx && (
                    <>
                      <span className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]"
                        data-testid={`pos-open-till-waiting-${t.id}`}>
                        <Clock className="w-3 h-3 inline mr-0.5" /> Waiting on cashier
                      </span>
                      <button type="button" onClick={() => onManagerTakeover(t)}
                        data-testid={`pos-open-till-manager-takeover-${t.id}`}
                        className="h-9 px-3 rounded border border-slate-400 text-slate-900 text-[11px] font-semibold hover:bg-slate-100">
                        <ShieldCheck className="w-3 h-3 inline mr-0.5" /> Manager takeover
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {tills.length === 0 && (
            <div className="text-center text-[12px] text-emerald-800 py-6" data-testid="pos-open-tills-empty">
              <CheckCircle2 className="w-5 h-5 inline mr-1" /> No open tills — ready to close the shift.
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between gap-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
          <button type="button" onClick={() => onWaitAgain("refresh")}
            data-testid="pos-open-tills-refresh"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            <RefreshCcw className="w-3 h-3 inline mr-1" /> Refresh
          </button>
          <button type="button" onClick={() => onWaitAgain("cancel")}
            data-testid="pos-open-tills-cancel"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            <XCircle className="w-3 h-3 inline mr-1" /> Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── ShiftCloseSummaryDialog ─────────────────────────────────────
// Aggregates the closed-till reconciliation records for the shift and
// displays them as a single ledger. Manager PIN + confirm to fire the
// existing close-shift endpoint.
function ShiftCloseSummaryDialog({ open, closedTills, shift, onCancel, onConfirm, requestAuth }) {
  if (!open) return null;
  const totals = closedTills.reduce((acc, t) => {
    acc.opening_float += Number(t.opening_float || 0);
    acc.cash_sales    += Number(t.settlement_mode_summary?.cash_sales || t.cash_sales || 0);
    acc.expected      += Number(t.expected_cash || 0);
    acc.counted       += Number(t.counted_cash  || t.declared_cash || 0);
    acc.variance      += Number(t.variance      || t.cash_variance || 0);
    return acc;
  }, { opening_float: 0, cash_sales: 0, expected: 0, counted: 0, variance: 0 });
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[860px] w-[94vw] max-h-[92vh] p-0 gap-0 border border-slate-300 overflow-hidden flex flex-col"
        data-testid="pos-shift-close-summary">
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] bg-slate-50 space-y-0 shrink-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Close shift</DialogTitle>
          <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            Cash was reconciled per till. Shift Close is a service-shift terminator only — no drawer count here.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-[12px] tabular-nums" data-testid="pos-shift-close-summary-table">
            <thead className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
              <tr className="border-b border-[color:var(--pos-color-border,#E2E8DF)]">
                <th className="text-left py-1.5 pr-2">Cashier</th>
                <th className="text-right pr-2">Opening float</th>
                <th className="text-right pr-2">Cash sales</th>
                <th className="text-right pr-2">Expected</th>
                <th className="text-right pr-2">Counted</th>
                <th className="text-right pr-2">Variance</th>
                <th className="text-left pl-2">Method</th>
              </tr>
            </thead>
            <tbody>
              {closedTills.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  No tills were opened this shift.
                </td></tr>
              )}
              {closedTills.map((t) => (
                <tr key={t.id || t.till_id} className="border-b border-[color:var(--pos-color-border,#E2E8DF)]"
                  data-testid={`pos-shift-close-row-${t.id || t.till_id}`}>
                  <td className="py-1.5 pr-2">{t.staff_name || t.cashier_name || t.closed_by || "—"}</td>
                  <td className="text-right pr-2">₹{fmt2(t.opening_float)}</td>
                  <td className="text-right pr-2">₹{fmt2(t.settlement_mode_summary?.cash_sales || 0)}</td>
                  <td className="text-right pr-2">₹{fmt2(t.expected_cash || 0)}</td>
                  <td className="text-right pr-2">₹{fmt2(t.counted_cash || t.declared_cash || 0)}</td>
                  <td className={`text-right pr-2 ${(t.variance || t.cash_variance || 0) < 0 ? "text-rose-800" : (t.variance || t.cash_variance || 0) > 0 ? "text-amber-800" : ""}`}>
                    ₹{fmt2(t.variance || t.cash_variance || 0)}
                  </td>
                  <td className="pl-2 text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                    {t.close_method || "self"}
                  </td>
                </tr>
              ))}
              {closedTills.length > 0 && (
                <tr className="font-semibold" data-testid="pos-shift-close-totals">
                  <td className="py-1.5 pr-2">Shift totals</td>
                  <td className="text-right pr-2">₹{fmt2(totals.opening_float)}</td>
                  <td className="text-right pr-2">₹{fmt2(totals.cash_sales)}</td>
                  <td className="text-right pr-2">₹{fmt2(totals.expected)}</td>
                  <td className="text-right pr-2">₹{fmt2(totals.counted)}</td>
                  <td className="text-right pr-2">₹{fmt2(totals.variance)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
          <button type="button" onClick={onCancel}
            data-testid="pos-shift-close-cancel"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-card-bg,#FFFFFF)]">
            Cancel
          </button>
          <button type="button"
            data-testid="pos-shift-close-confirm"
            onClick={() => requestAuth(
              `Close shift ${shift?.id ? shift.id.slice(0, 6) + "…" : ""} · ${closedTills.length} closed till${closedTills.length === 1 ? "" : "s"}`,
              async (_auth) => { onConfirm({ closedTills, totals }); },
            )}
            className="h-9 px-4 rounded bg-emerald-800 text-white text-[12px] font-semibold hover:bg-emerald-900">
            <ShieldCheck className="w-3 h-3 inline mr-1" /> Manager PIN → Close shift
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── TillShiftCloseFlow (orchestrator) ────────────────────────────
export function TillShiftCloseFlow({
  open, currentStaffId, shift, livePos, onCancel, onShiftClosed, requestAuth,
  outletName,
}) {
  const [tills, setTills] = useState([]);
  const [closedTills, setClosedTills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCloseTill, setActiveCloseTill] = useState(null);
  const [activeCloseMode, setActiveCloseMode] = useState("SELF");
  const [floatReturnTill, setFloatReturnTill] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    if (!livePos) { setTills([]); setLoading(false); return; }
    // Server is the SINGLE source of truth for BOTH lists. The
    // open-till list drives the blocker; the closed-till list feeds
    // the Shift Close summary so a cashier who self-closed on another
    // terminal still appears in the ledger the manager sees here.
    const [openList, closedList] = await Promise.all([
      livePos.listTills(),
      livePos.listClosedTills ? livePos.listClosedTills() : Promise.resolve([]),
    ]);
    setTills(openList);
    setClosedTills(closedList);
    setLoading(false);
  }, [livePos]);

  useEffect(() => {
    if (!open) return;
    refresh();
    // Poll every 5 s so a cashier closing on another terminal drops
    // off the blocker without a manual refresh. Stops when the
    // dialog closes.
    pollRef.current = setInterval(refresh, 5000);
    return () => clearInterval(pollRef.current);
  }, [open, refresh]);

  const onSelfClose = (till) => {
    setActiveCloseTill(till);
    setActiveCloseMode("SELF");
  };
  const onManagerTakeover = async (till) => {
    // Lock the till first (records takeover_active_at) so the cashier
    // terminal starts refusing tenders while the manager counts.
    if (livePos) {
      const lock = await livePos.tillTakeoverLock(till.id);
      if (!lock?.ok) return;
    }
    setActiveCloseTill({ ...till, takeover_active_at: Date.now() });
    setActiveCloseMode("MANAGER_TAKEOVER");
  };
  const onFloatReturn = (till) => setFloatReturnTill(till);

  const handleTillClosed = async (payload) => {
    if (!livePos) return;
    const r = await livePos.closeTill(payload.counted_cash || 0, payload);
    if (r && r.till_id) {
      setActiveCloseTill(null);
      setFloatReturnTill(null);
      // Server refresh — closed till appears in listClosedTills() and
      // drops off listTills() atomically.
      setTimeout(refresh, 200);
    }
  };

  const noOpenTills = !loading && tills.length === 0;
  const showBlocker = open && !activeCloseTill && !floatReturnTill && !noOpenTills;
  const showSummary = open && !activeCloseTill && !floatReturnTill && noOpenTills;

  return (
    <>
      <OpenTillsBlocker open={showBlocker}
        tills={tills}
        currentStaffId={currentStaffId}
        onWaitAgain={(what) => (what === "refresh" ? refresh() : onCancel())}
        onSelfClose={onSelfClose}
        onManagerTakeover={onManagerTakeover}
        onFloatReturn={onFloatReturn} />
      <TillCloseDenominationDialog
        open={!!activeCloseTill}
        till={activeCloseTill}
        mode={activeCloseMode}
        requestAuth={requestAuth}
        onCancel={() => setActiveCloseTill(null)}
        onSubmit={handleTillClosed} />
      <FloatReturnDialog open={!!floatReturnTill}
        till={floatReturnTill}
        requestAuth={requestAuth}
        onCancel={() => setFloatReturnTill(null)}
        onSubmit={handleTillClosed} />
      <ShiftCloseSummaryDialog open={showSummary}
        closedTills={closedTills}
        shift={shift}
        requestAuth={requestAuth}
        onCancel={onCancel}
        onConfirm={async ({ totals }) => {
          onShiftClosed({ closedTills, totals });
        }} />
    </>
  );
}
